/**
 * Admin section for Git server management — P6b-1 (Servers panel) + P6b-2 (Overview table).
 *
 * Security properties:
 * - access_token is WRITE-ONLY: the server never returns it; we never display it.
 * - Token input fields always use type="password".
 * - has_token indicates whether a token is stored without exposing its value.
 *
 * P6b-2: GitOverviewTable renders above the servers panel.
 * Proposal apply/dismiss is intentionally out of scope — the admin overview
 * shows the pending COUNT and links to the per-project UI (P4c/P5).
 */
import { useEffect, useState, useCallback, useRef } from "preact/hooks";
import { CheckCircle2, XCircle, ChevronDown, ChevronRight, Trash2, Plus } from "lucide-preact";
import { toastError, toastSuccess } from "../../lib/toast";
import { GitUserMapSection } from "./GitUserMapSection";
import {
  listGitServers,
  createGitServer,
  updateGitServer,
  deleteGitServer,
  testGitServer,
  discoverGitServerRepos,
  listGitRepositories,
  getReconcileOverview,
  getAdminLinkRuns,
  adminTriggerReconcile,
  type AdminGitServer,
  type AdminGitRepository,
  type ReconcileOverviewRow,
  type AdminReconcileRun,
  type ReconcileRunCounts,
} from "../../lib/api";

// ---------------------------------------------------------------------------
// Shared style constants — mirroring AiSettingsSection
// ---------------------------------------------------------------------------

const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const LABEL_CLS =
  "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";

const BTN_PRIMARY =
  "rounded bg-[color:var(--color-primary)] px-3 py-2 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const BTN_SECONDARY =
  "rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const BTN_DANGER =
  "rounded bg-[color:var(--color-error)] px-2 py-1 text-xs text-[color:var(--color-on-error)] hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] transition-colors";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a counts object into a compact readable string.
 * E.g. { proposed_create: 2, proposed_close: 0, applied: 1 } -> "erstellt: 2, angewandt: 1"
 */
function formatCounts(counts: ReconcileRunCounts): string {
  const parts: string[] = [];
  if (counts.proposed_create != null && counts.proposed_create > 0) {
    parts.push(`+${counts.proposed_create}`);
  }
  if (counts.proposed_close != null && counts.proposed_close > 0) {
    parts.push(`-${counts.proposed_close}`);
  }
  if (counts.applied != null && counts.applied > 0) {
    parts.push(`angewandt: ${counts.applied}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}

/** Map a run status to a compact display label and colour class. */
function runStatusDisplay(status: string): { label: string; cls: string } {
  switch (status) {
    case "success":
      return { label: "success", cls: "text-[color:var(--color-primary)]" };
    case "error":
    case "failed":
      return { label: status, cls: "text-[color:var(--color-error)]" };
    case "running":
      return { label: "läuft…", cls: "text-[color:var(--color-on-surface-variant)]" };
    default:
      return { label: status, cls: "text-[color:var(--color-on-surface-variant)]" };
  }
}

// ---------------------------------------------------------------------------
// Run-history expander row
// ---------------------------------------------------------------------------

interface RunHistoryProps {
  linkId: number;
  onClose: () => void;
}

function RunHistoryPanel({ linkId, onClose }: RunHistoryProps): preact.JSX.Element {
  const [runs, setRuns] = useState<AdminReconcileRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAdminLinkRuns(linkId)
      .then((r) => {
        if (!cancelled) setRuns(r);
      })
      .catch(() => {
        // GET-Fehler werden von api() nicht getoastet — ohne Hinweis waere ein
        // leerer Verlauf nicht von einem gescheiterten Abruf zu unterscheiden.
        if (!cancelled) {
          setRuns([]);
          toastError("Verlauf konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  return (
    <div class="mt-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] overflow-hidden">
      <div class="flex items-center justify-between px-3 py-2 border-b border-[color:var(--color-outline-variant)]">
        <span class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
          Verlauf
        </span>
        <button
          type="button"
          onClick={onClose}
          class="text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] transition-colors"
          aria-label="Verlauf schließen"
        >
          schließen
        </button>
      </div>
      {loading ? (
        <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)]">Lädt Verlauf…</p>
      ) : runs.length === 0 ? (
        <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)] italic">Noch keine Läufe.</p>
      ) : (
        <ul class="divide-y divide-[color:var(--color-outline-variant)] max-h-48 overflow-y-auto">
          {runs.map((run) => {
            const sd = runStatusDisplay(run.status);
            return (
              <li key={run.id} class="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-xs">
                <span class={`font-mono font-medium ${sd.cls}`}>
                  {sd.label}
                </span>
                <span class="text-[color:var(--color-on-surface-variant)] opacity-70">
                  {run.trigger}
                </span>
                <span class="text-[color:var(--color-on-surface-variant)] opacity-70">
                  {run.finished_at ? new Date(run.finished_at).toLocaleString("de-DE") : "—"}
                </span>
                <span class="text-[color:var(--color-on-surface-variant)]">
                  {formatCounts(run.counts)}
                </span>
                {run.summary && (
                  <span class="w-full truncate text-[color:var(--color-on-surface-variant)] opacity-80">
                    {run.summary}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview table row
// ---------------------------------------------------------------------------

interface OverviewRowProps {
  row: ReconcileOverviewRow;
  onTriggered: () => void;
}

function OverviewTableRow({ row, onTriggered }: OverviewRowProps): preact.JSX.Element {
  const [triggering, setTriggering] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleTrigger = async (): Promise<void> => {
    setTriggering(true);
    try {
      const result = await adminTriggerReconcile(row.link_id);
      const countsStr = formatCounts(result.counts);
      toastSuccess(`Abgleich gestartet — Status: ${result.status}${countsStr !== "—" ? ` (${countsStr})` : ""}`);
      onTriggered();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Abgleich fehlgeschlagen");
    } finally {
      setTriggering(false);
    }
  };

  const latestRun = row.latest_run;
  const runSd = latestRun ? runStatusDisplay(latestRun.status) : null;

  return (
    <li class="border border-[color:var(--color-outline-variant)] rounded-lg bg-[color:var(--color-surface-container-lowest)] p-3 space-y-2">
      {/* Main info row */}
      <div class="flex flex-wrap items-start gap-x-4 gap-y-1">
        {/* Project + repo */}
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-[color:var(--color-on-surface)] truncate">
            {row.project_name}
          </p>
          <p class="text-xs font-mono text-[color:var(--color-on-surface-variant)] truncate">
            {row.repository_full_name}
          </p>
        </div>

        {/* Server + provider */}
        <div class="shrink-0 text-right">
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">{row.server_name}</p>
          <span class="inline-block rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
            {row.provider}
          </span>
        </div>

        {/* AI automation status */}
        <div class="shrink-0">
          {row.ai_automation_enabled ? (
            <span class="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-primary-container)] px-2 py-0.5 text-xs text-[color:var(--color-on-primary-container)]">
              <CheckCircle2 size={14} aria-hidden="true" />
              KI-Automatisierung an
            </span>
          ) : (
            <span class="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
              <XCircle size={14} aria-hidden="true" />
              KI-Automatisierung aus
            </span>
          )}
        </div>
      </div>

      {/* Run status + pending */}
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {latestRun && runSd ? (
          <>
            <span>
              Status:{" "}
              <span class={`font-mono font-medium ${runSd.cls}`}>{runSd.label}</span>
            </span>
            {latestRun.finished_at && (
              <span class="text-[color:var(--color-on-surface-variant)] opacity-70">
                {new Date(latestRun.finished_at).toLocaleString("de-DE")}
              </span>
            )}
            <span class="text-[color:var(--color-on-surface-variant)]">
              {formatCounts(latestRun.counts)}
            </span>
          </>
        ) : (
          <span class="text-[color:var(--color-on-surface-variant)] opacity-60 italic">
            Noch kein Lauf
          </span>
        )}

        {/* Pending proposals badge */}
        {row.pending_proposal_count > 0 && (
          <span class="rounded-full bg-[color:var(--color-secondary-container)] px-2 py-0.5 text-[color:var(--color-on-secondary-container)] font-medium">
            {row.pending_proposal_count} Vorschläge offen
          </span>
        )}
      </div>

      {/* Actions */}
      <div class="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => void handleTrigger()}
          disabled={triggering}
          class={`${BTN_PRIMARY} text-xs px-2 py-1`}
          aria-label={`Abgleichen für ${row.project_name}`}
        >
          {triggering ? "Läuft…" : "Abgleichen"}
        </button>

        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          class={`${BTN_SECONDARY} inline-flex items-center gap-1 text-xs px-2 py-1`}
          aria-label={`Verlauf für ${row.project_name}`}
          aria-expanded={historyOpen}
        >
          {historyOpen ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          Runs / Verlauf
        </button>

        {row.pending_proposal_count > 0 && (
          <a
            href={`/p/${row.project_id}?settings=git`}
            class="text-xs text-[color:var(--color-primary)] hover:underline"
          >
            Vorschläge im Projekt prüfen
          </a>
        )}
      </div>

      {/* Run-history panel */}
      {historyOpen && (
        <RunHistoryPanel
          linkId={row.link_id}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Git overview table (P6b-2)
// ---------------------------------------------------------------------------

/**
 * Cross-project reconcile overview table.
 *
 * Displays one row per project_repo_link with: project name, repository full name,
 * server name + provider badge, AI automation status, latest run summary,
 * pending proposal count, a manual "Abgleichen" trigger, and an expandable run-history.
 *
 * Proposal apply/dismiss is intentionally out of scope here — the pending count
 * badge links to the per-project UI (P4c/P5).
 */
function GitOverviewTable(): preact.JSX.Element {
  const [rows, setRows] = useState<ReconcileOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadError(null);
    getReconcileOverview()
      .then((r) => {
        if (mountedRef.current) setRows(r);
      })
      .catch((err: unknown) => {
        if (mountedRef.current)
          setLoadError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt Übersicht…</p>
    );
  }

  if (loadError) {
    return (
      <p class="text-sm text-[color:var(--color-error)]">Fehler: {loadError}</p>
    );
  }

  return (
    <div>
      <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] mb-3">
        Verknüpfte Repositories — Übersicht
      </h2>

      {rows.length === 0 ? (
        <p class="text-sm text-[color:var(--color-on-surface-variant)] italic mb-4">
          Keine verknüpften Repositories
        </p>
      ) : (
        <ul class="space-y-3 mb-4">
          {rows.map((row) => (
            <OverviewTableRow key={row.link_id} row={row} onTriggered={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-server form
// ---------------------------------------------------------------------------

interface AddServerFormProps {
  onCreated: (server: AdminGitServer) => void;
}

function AddServerForm({ onCreated }: AddServerFormProps): preact.JSX.Element {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState<"github" | "gitlab_selfhosted">("github");
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setSaving(true);
    try {
      const body: Parameters<typeof createGitServer>[0] = {
        provider,
        name: trimmedName,
        enabled: true,
      };
      if (baseUrl.trim()) body.base_url = baseUrl.trim();
      if (token.trim()) body.access_token = token.trim();

      const created = await createGitServer(body);
      onCreated(created);
      setName("");
      setBaseUrl("");
      setToken("");
      toastSuccess(`Git-Server „${created.name}" angelegt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4 space-y-4">
      <h3 class="text-sm font-medium text-[color:var(--color-on-surface)]">
        Neuen Git-Server hinzufügen
      </h3>

      <div>
        <label class={LABEL_CLS} for="gs-name">
          Name
        </label>
        <input
          id="gs-name"
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          placeholder="z.B. GitHub Org"
          class={INPUT_CLS}
          aria-label="Name"
        />
      </div>

      <div>
        <label class={LABEL_CLS} for="gs-provider">
          Anbieter (Provider)
        </label>
        <select
          id="gs-provider"
          value={provider}
          onChange={(e) =>
            setProvider((e.target as HTMLSelectElement).value as "github" | "gitlab_selfhosted")
          }
          class={SELECT_CLS}
          aria-label="Provider"
        >
          <option value="github">GitHub</option>
          <option value="gitlab_selfhosted">GitLab (Self-hosted)</option>
        </select>
      </div>

      <div>
        <label class={LABEL_CLS} for="gs-base-url">
          Basis-URL{" "}
          {provider === "gitlab_selfhosted" ? (
            <span class="text-[color:var(--color-error)]">*</span>
          ) : (
            <span class="font-normal opacity-60">(optional, Standard: https://github.com)</span>
          )}
        </label>
        <input
          id="gs-base-url"
          type="url"
          value={baseUrl}
          onInput={(e) => setBaseUrl((e.target as HTMLInputElement).value)}
          placeholder={
            provider === "gitlab_selfhosted"
              ? "https://git.example.com"
              : "https://github.com"
          }
          class={INPUT_CLS}
          aria-label="Basis-URL"
        />
      </div>

      <div>
        <label class={LABEL_CLS} for="gs-token">
          Access Token{" "}
          <span class="font-normal opacity-60">(write-only; optional jetzt, später editierbar)</span>
        </label>
        <input
          id="gs-token"
          type="password"
          name="token"
          value={token}
          onInput={(e) => setToken((e.target as HTMLInputElement).value)}
          placeholder="glpat-… / ghp_…"
          autocomplete="new-password"
          class={INPUT_CLS}
          aria-label="Access Token"
        />
      </div>

      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={!name.trim() || saving}
        class={`${BTN_PRIMARY} inline-flex items-center gap-1.5`}
        aria-label="Server hinzufügen"
      >
        <Plus size={14} aria-hidden="true" />
        {saving ? "Erstellt…" : "Server hinzufügen"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-server row
// ---------------------------------------------------------------------------

interface ServerRowProps {
  server: AdminGitServer;
  onDeleted: (id: number) => void;
  onUpdated: (server: AdminGitServer) => void;
}

function ServerRow({ server, onDeleted, onUpdated }: ServerRowProps): preact.JSX.Element {
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; repo_count?: number } | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [reposExpanded, setReposExpanded] = useState(false);
  const [repos, setRepos] = useState<AdminGitRepository[]>([]);
  const [reposLoading, setReposLoading] = useState(false);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(server.name);
  const [editBaseUrl, setEditBaseUrl] = useState(server.base_url);
  const [editEnabled, setEditEnabled] = useState(server.enabled);
  const [editToken, setEditToken] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      await deleteGitServer(server.id);
      onDeleted(server.id);
      toastSuccess(`Git-Server „${server.name}" gelöscht.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  const handleTest = async (): Promise<void> => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testGitServer(server.id);
      setTestResult(result);
    } catch (err) {
      setTestResult({ ok: false });
      toastError(err instanceof Error ? err.message : "Verbindungstest fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const handleDiscover = async (): Promise<void> => {
    setDiscovering(true);
    try {
      const result = await discoverGitServerRepos(server.id);
      toastSuccess(`${result.discovered} Repos abgerufen und gespeichert.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Abrufen fehlgeschlagen");
    } finally {
      setDiscovering(false);
    }
  };

  const handleToggleRepos = async (): Promise<void> => {
    if (reposExpanded) {
      setReposExpanded(false);
      return;
    }
    setReposExpanded(true);
    setReposLoading(true);
    try {
      const list = await listGitRepositories(server.id);
      setRepos(list);
    } catch {
      // Non-fatal — just show nothing
      setRepos([]);
    } finally {
      setReposLoading(false);
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    setSavingEdit(true);
    try {
      const patch: Parameters<typeof updateGitServer>[1] = {
        name: editName.trim(),
        enabled: editEnabled,
      };
      if (editBaseUrl.trim()) patch.base_url = editBaseUrl.trim();
      if (editToken.trim()) patch.access_token = editToken.trim();

      const updated = await updateGitServer(server.id, patch);
      onUpdated(updated);
      setEditing(false);
      setEditToken("");
      toastSuccess("Git-Server aktualisiert.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <li class="border border-[color:var(--color-outline-variant)] rounded-lg bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
      {/* Header row */}
      <div class="flex flex-wrap items-center gap-2 px-4 py-3">
        {/* Name + badges */}
        <span class="font-medium text-sm text-[color:var(--color-on-surface)] flex-1 min-w-0 truncate">
          {server.name}
        </span>

        {/* Provider badge */}
        <span class="shrink-0 rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
          {server.provider}
        </span>

        {/* Enabled indicator */}
        <span
          class={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
            server.enabled
              ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
              : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
          }`}
        >
          {server.enabled ? "aktiv" : "inaktiv"}
        </span>

        {/* Token indicator */}
        <span
          class={`shrink-0 text-xs font-mono ${
            server.has_token
              ? "text-[color:var(--color-primary)]"
              : "text-[color:var(--color-on-surface-variant)] opacity-60"
          }`}
        >
          {server.has_token ? "Token gesetzt ✓" : "kein Token"}
        </span>
      </div>

      {/* Base URL */}
      <div class="px-4 pb-2 text-xs text-[color:var(--color-on-surface-variant)] truncate">
        {server.base_url}
      </div>

      {/* Edit form */}
      {editing && (
        <div class="px-4 pb-3 space-y-3 border-t border-[color:var(--color-outline-variant)] pt-3">
          <div>
            <label class={LABEL_CLS} for={`gs-edit-name-${server.id}`}>
              Name
            </label>
            <input
              id={`gs-edit-name-${server.id}`}
              type="text"
              value={editName}
              onInput={(e) => setEditName((e.target as HTMLInputElement).value)}
              class={INPUT_CLS}
            />
          </div>
          <div>
            <label class={LABEL_CLS} for={`gs-edit-url-${server.id}`}>
              Basis-URL
            </label>
            <input
              id={`gs-edit-url-${server.id}`}
              type="url"
              value={editBaseUrl}
              onInput={(e) => setEditBaseUrl((e.target as HTMLInputElement).value)}
              class={INPUT_CLS}
            />
          </div>
          <div class="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={editEnabled}
              aria-label="Aktiv"
              onClick={() => setEditEnabled((v) => !v)}
              class={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                editEnabled
                  ? "bg-[color:var(--color-primary)]"
                  : "bg-[color:var(--color-surface-container-high)]"
              }`}
            >
              <span
                aria-hidden="true"
                class={`pointer-events-none inline-block h-4 w-4 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] transition-transform ${
                  editEnabled ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
            <span class="text-sm text-[color:var(--color-on-surface)]">Aktiv</span>
          </div>
          <div>
            <label class={LABEL_CLS} for={`gs-edit-token-${server.id}`}>
              Token rotieren{" "}
              <span class="font-normal opacity-60">
                (leer lassen = bestehendes Token behalten)
              </span>
            </label>
            <input
              id={`gs-edit-token-${server.id}`}
              type="password"
              name="token"
              value={editToken}
              onInput={(e) => setEditToken((e.target as HTMLInputElement).value)}
              placeholder="Neues Token…"
              autocomplete="new-password"
              class={INPUT_CLS}
            />
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => void handleSaveEdit()}
              disabled={!editName.trim() || savingEdit}
              class={BTN_PRIMARY}
            >
              {savingEdit ? "Speichert…" : "Speichern"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEditToken("");
              }}
              class={BTN_SECONDARY}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Test result */}
      {testResult !== null && (
        <div
          class={`mx-4 mb-2 flex items-center gap-2 rounded border px-3 py-2 text-xs ${
            testResult.ok
              ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
              : "border-[color:var(--color-error)] bg-[color:var(--color-error-container)] text-[color:var(--color-on-error-container)]"
          }`}
        >
          {testResult.ok ? (
            <CheckCircle2 size={14} aria-hidden="true" />
          ) : (
            <XCircle size={14} aria-hidden="true" />
          )}
          <span>
            {testResult.ok
              ? `Verbindung ok${testResult.repo_count !== undefined ? ` — ${testResult.repo_count} Repos sichtbar` : ""}`
              : "Verbindung fehlgeschlagen"}
          </span>
        </div>
      )}

      {/* Discovered repos list */}
      {reposExpanded && (
        <div class="mx-4 mb-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] overflow-hidden">
          {reposLoading ? (
            <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)]">Lädt Repos…</p>
          ) : repos.length === 0 ? (
            <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
              Noch keine Repos — „Repos abrufen" ausführen.
            </p>
          ) : (
            <ul class="divide-y divide-[color:var(--color-outline-variant)] max-h-48 overflow-y-auto">
              {repos.map((r) => (
                <li key={r.id} class="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <span class="flex-1 font-mono truncate text-[color:var(--color-on-surface)]">
                    {r.full_name}
                  </span>
                  {r.default_branch && (
                    <span class="shrink-0 text-[color:var(--color-on-surface-variant)] opacity-70">
                      {r.default_branch}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div class="flex flex-wrap items-center gap-2 px-4 pb-3">
        <button
          type="button"
          onClick={() => void handleTest()}
          disabled={testing}
          class={BTN_SECONDARY}
          aria-label={`Verbindung testen für ${server.name}`}
        >
          {testing ? "Teste…" : "Verbindung testen"}
        </button>

        <button
          type="button"
          onClick={() => void handleDiscover()}
          disabled={discovering}
          class={BTN_SECONDARY}
          aria-label={`Repos abrufen für ${server.name}`}
        >
          {discovering ? "Abrufe…" : "Repos abrufen"}
        </button>

        <button
          type="button"
          onClick={() => void handleToggleRepos()}
          class={`${BTN_SECONDARY} inline-flex items-center gap-1`}
          aria-label={`Repos anzeigen für ${server.name}`}
          aria-expanded={reposExpanded}
        >
          {reposExpanded ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          Repos
        </button>

        {!editing && (
          <button
            type="button"
            onClick={() => {
              setEditName(server.name);
              setEditBaseUrl(server.base_url);
              setEditEnabled(server.enabled);
              setEditToken("");
              setEditing(true);
            }}
            class={BTN_SECONDARY}
          >
            Bearbeiten
          </button>
        )}

        {/* Delete with confirm */}
        {confirmDel ? (
          <>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              class={BTN_DANGER}
              aria-label={`${server.name} wirklich löschen`}
            >
              {deleting ? "Löscht…" : "Wirklich löschen"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              class={BTN_SECONDARY}
              aria-label="Abbrechen"
            >
              Abbrechen
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)]"
            aria-label={`${server.name} löschen`}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main exported section
// ---------------------------------------------------------------------------

/**
 * Git admin section rendered under /admin/git.
 *
 * P6b-1: Git Servers management panel.
 * P6b-2: GitOverviewTable — cross-project repo overview + manual trigger above the servers panel.
 */
export function GitAdminSection(): preact.JSX.Element {
  const [servers, setServers] = useState<AdminGitServer[]>([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [serversError, setServersError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load(): Promise<void> {
    setServersLoading(true);
    setServersError(null);
    try {
      const list = await listGitServers();
      setServers(list);
    } catch (err) {
      setServersError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
    } finally {
      setServersLoading(false);
    }
  }

  const handleCreated = (server: AdminGitServer): void => {
    setServers((prev) => [...prev, server]);
  };

  const handleDeleted = (id: number): void => {
    setServers((prev) => prev.filter((s) => s.id !== id));
  };

  const handleUpdated = (updated: AdminGitServer): void => {
    setServers((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  return (
    <section class="space-y-8 max-w-3xl">
      {/* Overview table (P6b-2) — cross-project repo overview + trigger */}
      <GitOverviewTable />

      {/* Servers panel (P6b-1) */}
      <div>
        <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] mb-3">
          Git-Server
        </h2>

        {serversLoading ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt Git-Server…</p>
        ) : serversError ? (
          <p class="text-sm text-[color:var(--color-error)]">Fehler: {serversError}</p>
        ) : servers.length === 0 ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic mb-4">
            Noch keine Server konfiguriert.
          </p>
        ) : (
          <ul class="space-y-3 mb-4">
            {servers.map((s) => (
              <ServerRow
                key={s.id}
                server={s}
                onDeleted={handleDeleted}
                onUpdated={handleUpdated}
              />
            ))}
          </ul>
        )}

        {!serversLoading && !serversError && <AddServerForm onCreated={handleCreated} />}
      </div>

      {/* GitLab-user → juuplan-user mapping (reconcile CREATE-tier assignment) */}
      <GitUserMapSection />
    </section>
  );
}
